import { useCallback, useContext, useMemo, useState } from "react";
import { Box, Dialog, Grid } from "@mui/material";

import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import DownArrowIcon from "@icons/DownArrow";
import UpArrowIcon from "@icons/UpArrow";

import { checkAndUpdateDate } from "@src/utils/checkAndUpdateDate";
import dayjs from "dayjs";
import LocationContext from "@context/location/locationContext";
import Colors from "@constants/Colors";
import {
  StyledBox,
  StyledBoxHeading,
  StyledButton,
  StyledSelectionRow,
  StyledTypography,
  StyledYearCalendar,
  StyledDateCalendar,
  StyledRowButtons,
  StyledMonthCalendar,
  StyledCalenderBox,
  StyledActionButton,
  ButtonBox,
} from "./styles";

interface DateSelectorProps {
  minDate: string;
  date: string;
  open: boolean;
  onClose: (value: string) => void;
}

const DateSelectorDialog = ({
  minDate,
  date,
  open,
  onClose,
}: DateSelectorProps) => {
  const [selectedDate, setSelectedDate] = useState(date);
  const [showMonth, setShowMonth] = useState(false);
  const [showYear, setShowYear] = useState(false);

  const [{ locationState, locationSetState }] = useContext(LocationContext);

  const formattedDate = useMemo(
    () => dayjs(selectedDate).format("MMMM DD, YYYY"),
    [selectedDate]
  );
  const month = useMemo(
    () => dayjs(selectedDate).format("MMMM"),
    [selectedDate]
  );
  const year = useMemo(
    () => dayjs(selectedDate).format("YYYY"),
    [selectedDate]
  );
  const dateFormat = "YYYY-MM-DD";
  const updatedDate = checkAndUpdateDate();

  const handleSelectionClick = (selection: string) => {
    if (selection === "month") {
      setShowMonth(true);
      setShowYear(false);
    } else if (selection === "year") {
      setShowMonth(false);
      setShowYear(true);
    }
  };

  const checkDiff = (value: string) => {
    const minDiff = dayjs(value).diff(minDate);
    if (minDiff < 0) {
      setSelectedDate(dayjs(minDate).format(dateFormat));
    } else {
      setSelectedDate(dayjs(value).format(dateFormat));
    }
    setShowMonth(false);
    setShowYear(false);
  };

  const handleSubmit = useCallback(() => {
    localStorage.setItem("date", JSON.stringify(selectedDate));
    locationSetState({ ...locationState, date: selectedDate });
    onClose(selectedDate);
  }, [selectedDate, locationState]);

  return (
    <Dialog open={open} onClose={handleSubmit}>
      <StyledBox>
        <StyledBoxHeading>
          <StyledTypography>Select a Date</StyledTypography>
        </StyledBoxHeading>
        <StyledBoxHeading>
          <StyledTypography date={formattedDate}>
            {formattedDate}
          </StyledTypography>
        </StyledBoxHeading>
      </StyledBox>
      <StyledSelectionRow>
        <ButtonBox>
          <StyledButton
            variant={showMonth ? "contained" : "outlined"}
            selected={showMonth}
            onClick={() => handleSelectionClick("month")}
          >
            {month}
            {showMonth ? (
              <UpArrowIcon
                strokeColor={showMonth ? Colors.white : Colors.primary_600}
              />
            ) : (
              <DownArrowIcon
                strokeColor={showMonth ? Colors.white : Colors.primary_600}
              />
            )}
          </StyledButton>
        </ButtonBox>
        <ButtonBox>
          <StyledButton
            variant={showYear ? "contained" : "outlined"}
            selected={showYear}
            onClick={() => handleSelectionClick("year")}
          >
            {year}
            {showYear ? (
              <UpArrowIcon
                strokeColor={showYear ? Colors.white : Colors.primary_600}
              />
            ) : (
              <DownArrowIcon
                strokeColor={showYear ? Colors.white : Colors.primary_600}
              />
            )}
          </StyledButton>
        </ButtonBox>
      </StyledSelectionRow>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={["DateCalendar", "YearCalendar"]}>
          <StyledCalenderBox>
            {!showYear && !showMonth && (
              <StyledDateCalendar
                fixedWeekNumber={6}
                minDate={dayjs(minDate)}
                views={["day"]}
                value={dayjs(selectedDate)}
                onChange={(value: any | null) => {
                  setSelectedDate(dayjs(value).format(dateFormat));
                }}
                dayOfWeekFormatter={(day) => day.slice(0, 2)}
                openTo="day"
              />
            )}
            {showYear && (
              <StyledYearCalendar
                autoFocus
                minDate={dayjs(minDate)}
                value={dayjs(selectedDate)}
                onChange={(value: any | null) => {
                  checkDiff(value);
                }}
              />
            )}
            {showMonth && (
              <StyledMonthCalendar
                minDate={dayjs(minDate)}
                value={dayjs(selectedDate)}
                onChange={(value: any | null) => {
                  checkDiff(value);
                }}
              />
            )}
          </StyledCalenderBox>
          <StyledRowButtons>
            <Grid>
              <StyledActionButton
                selected={false}
                onClick={() => {
                  onClose(date);
                }}
              >
                Cancel
              </StyledActionButton>
            </Grid>
            <Grid>
              <StyledActionButton
                selected
                variant="contained"
                onClick={handleSubmit}
              >
                Confirm
              </StyledActionButton>
            </Grid>
          </StyledRowButtons>
        </DemoContainer>
      </LocalizationProvider>
    </Dialog>
  );
};

export default DateSelectorDialog;
